import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import { useState, useCallback } from "react";
import { encode } from "@erikmichelson/open-location-code-ts";
import { startGlobalLoading, stopGlobalLoading } from "../utils/globalLoading";

export default function LocationPickerModal({
  apiKey,
  defaultCenter = { lat: 33.8938, lng: 35.5018 },
  zoom = 13,
  initialValue = null,
  onSelect,
  onClose,
}) {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey,
  });

  const [marker, setMarker] = useState(initialValue);
  const [loading, setLoading] = useState(false);
  const [locationLabel, setLocationLabel] = useState("");
  const mapContainerStyle = {
    borderRadius: "12px",
    width: "min(92vw, 450px)",
    height: "min(70vw, 450px)",
    minHeight: "280px",
    border: "5px solid #ffffff",
    boxSizing: "border-box",
  };

  // Handle map click
  const handleMapClick = useCallback((e) => {
    const coords = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    };
    setMarker(coords);
    reverseGeocode(coords.lat, coords.lng);
  }, []);

  // Handle "Use My Location"
  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      setLocationLabel("Geolocation not supported");
      return;
    }
    setLoading(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setMarker(coords);
        reverseGeocode(coords.lat, coords.lng);
      },
      (error) => {
        console.error("Location error:", error);
        setLocationLabel("Permission denied");
        setLoading(false);
      },
    );
  };

  // Reverse geocode lat/lng into structured location
  const reverseGeocode = async (lat, lng) => {
    setLoading(true);
    startGlobalLoading();

    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
      );
      const data = await res.json();
      const address = data.address || {};

      const plusCode = encode(lat, lng);

      const city = address.city || address.town || address.village || "";
      const postal = address.postcode || "";
      const label = `${plusCode}, ${city} ${postal}`;

      const locationData = {
        lat,
        lng,
        plusCode,
        label,
        city,
        postal,
        country: address.country || "",
        countryCode: address.country_code || "",
        state: address.state || "",
        stateDistrict: address.state_district || "",
        suburb: address.suburb || "",
        road: address.road || "",
        isoCode: address["ISO3166-2-lvl4"] || "",
        rawAddress: address,
      };

      setLocationLabel(label);
      if (onSelect) onSelect(locationData);
    } catch (err) {
      console.error(err);
      setLocationLabel("Error fetching location");
    } finally {
      setLoading(false);
      stopGlobalLoading();
    }
  };

  if (loadError) return <div>Map failed to load</div>;
  if (!isLoaded) return <div>Loading map...</div>;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        borderRadius: "15px",
        zIndex: 999,
        gap: "10px",
        width: "min(94vw, 600px)",
        maxWidth: "600px",
        padding: "0 8px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={marker || defaultCenter}
          zoom={zoom}
          onClick={handleMapClick}
        >
          {marker && <Marker position={marker} />}
        </GoogleMap>
      </div>
      <div
        style={{
          gap: "15px",
          marginTop: 10,
          width: "100%",
          maxWidth: 400,
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <button
          style={{
            all: "unset",
            fontWeight: "bolder",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#4072c8",
            color: "#ffff",
            height: "40px",
            borderRadius: "50px",
            width: "min(170px, 100%)",
            minWidth: "140px",
            textAlign: "center",
            cursor: "pointer",
          }}
          onClick={handleUseMyLocation}
        >
          {loading ? "Loading..." : "Use My Location"}
        </button>
        <button
          style={{
            all: "unset",
            fontWeight: "bolder",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#ffffff",
            boxShadow: " inset 0px -1px 3px rgba(0, 18, 71, 0.5)",
            color: "#424242",
            height: "40px",
            borderRadius: "50px",
            width: "min(120px, 100%)",
            minWidth: "110px",
            textAlign: "center",
            cursor: "pointer",
          }}
          onClick={() => onClose && onClose()}
        >
          Close
        </button>
      </div>
    </div>
  );
}
